/** @jsxImportSource theme-ui */
import classNames from 'classnames';
import { forwardRef, MutableRefObject } from 'react';
import { ThemeUIStyleObject } from 'theme-ui';

import theme from 'theme';

import { IClickable } from '../common-props';

export const TypographyColors = theme.colors;

const LegacyVariants = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'subtitle1',
  'subtitle2',
  'subtitle3',
  'body1',
  'body2',
  'caption',
  'caption2',
  'overline',
  'overline2',
  'button',
  'label-small',
  'source-code-pro',
] as const;

export const TypographyVariants = [
  'body-large-regular',
  'body-large-semiemphasized',
  'body-large-emphasized',
  'body-small-regular',
  'body-small-semiemphasized',
  'body-small-emphasized',
  'caption-regular',
  'caption-emphasized',
  ...LegacyVariants,
] as const;

export type VariantLiteral = typeof TypographyVariants[number]; // LESSON: ec070597: https://stackoverflow.com/a/45486495
export type TypographyColor = keyof typeof TypographyColors;
type TypographyProps = {
  /** The text font-variant */
  variant: VariantLiteral | VariantLiteral[];
  /** The colour of the text */
  color?: TypographyColor | TypographyColor[];
  /** Sets the text inline */
  inline?: boolean;
  /** Enforces no text wrapping on the text */
  nowrap?: boolean;
  /** The space below the text */
  marginBottom?: string;
  className?: string;
  children: React.ReactNode;
} & IClickable;

export const Typography = forwardRef<HTMLDivElement | HTMLSpanElement, TypographyProps>(
  (
    {
      variant,
      color = 'inherit',
      inline = false,
      nowrap = false,
      children,
      className,
      marginBottom,
      ...props
    },
    ref
  ) => {
    const sxShared: ThemeUIStyleObject = {
      color: color,
      ...(nowrap && { whiteSpace: 'nowrap' }),
    };

    const classes = classNames({
      'bd-typography': true,
      [`bd-typography-${variant}`]: !Array.isArray(variant),
      ...(Array.isArray(variant) && {
        ...(variant[0] && { [`bd-typography-${variant[0]}`]: true }),
        ...(variant[1] && { [`bd-typography-${variant[1]}-m`]: true }),
        ...(variant[2] && { [`bd-typography-${variant[2]}-l`]: true }),
        ...(variant[3] && { [`bd-typography-${variant[3]}-xl`]: true }),
      }),
      [className]: !!className,
    });

    // CODE: 28d11940: see typography definitions in `site.variables`
    return inline ? (
      <span ref={ref} className={classes} sx={sxShared} {...props}>
        {children}
      </span>
    ) : (
      <div
        ref={ref as MutableRefObject<HTMLDivElement>}
        className={classes}
        sx={{
          ...sxShared,
          ...(marginBottom && !inline && { '&.bd-typography': { mb: marginBottom } }), // NOTE: marginBottom only applies to non-inline (AKA block) elements
        }}
        {...props}
      >
        {children}
      </div>
    );
  }
);

export default Typography;
